import { GLOBAL_COUNTRY_ISO } from "@stadium/helpers/country/constants";
import { getProductListingData } from "@helpers/listing";
import { getLockerShopESQueryVars } from "@helpers/store";
import { getAllMenuTaxons, getTaxonIdsRecursively } from "@helpers/menu";

import {
  getIsHideBulkPricing,
  getIsLegacyLockerShop,
  getIsLockerShop,
  getIsPricelessShop,
  getStoreCacheKey,
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getStoreUpcharge,
} from "@selectors/store";

import { CUSTOM, DEFAULT } from "@helpers/plasmic/slices";
import { getFilterQueryVariablesFromFilterOptions } from "@helpers/products/search";

import { DEFAULT_MENU_ITEM_CODE } from "@constants/menu";
import { RECOMMENDED_SORT_ITEM } from "@constants/products/listing";
import { ALL_PRODUCTS } from "@constants/routes";

const DEFAULT_OBJ = {};
const emptyArr = [];

export const SEARCH_SOURCE = "search";

export const DEFAULT_PRODUCT_COUNT = 25;

export const queryVariables = ({
  store,
  menu,
  countryIso,
  menuMapping,
  categorySlug,
  productCount,
  identifier,
  viewType,
  filterOptions,
  skipAggregations = true,
}) => {
  const isLockerShop = getIsLockerShop({ store });
  const storeId = getStoreID({ store });
  const storeParentId = getStoreParentId({ store });
  const syncedToEs = getStoreSyncedToES({ store });
  const storeCloneId = getStoreCloneId({ store });
  const storeCacheKey = getStoreCacheKey({ store });
  const storeUpcharge = getStoreUpcharge({ store });
  const isHideBulkPricing = getIsHideBulkPricing({ store });
  const isLegacyLockerShop = getIsLegacyLockerShop({ store });
  const isPricelessShop = getIsPricelessShop({ store });

  const commonESVariables = {
    currentStoreId: storeId,
    storeUpcharge: storeUpcharge,
    excludeTierPricing: isHideBulkPricing,
  };

  const pricelessVariables = isPricelessShop
    ? {
        priceKeys: [`${store?.minPrice}_${store?.maxPrice}`],
        excludeByoProducts: true,
        excludeTierPricing: true,
        bulkAvailable: false,
      }
    : DEFAULT_OBJ;

  const selectedCategory = menuMapping[categorySlug ?? DEFAULT_MENU_ITEM_CODE];

  const menuLinkedResources = getTaxonIdsRecursively(selectedCategory);

  const ids = menuLinkedResources.map((resource) => +resource?.id);
  const withSwag = menuLinkedResources.some((resource) => resource?.forSwag);
  const newTaxonIds = ids.filter(Boolean);
  const taxonIds = newTaxonIds.length ? newTaxonIds : emptyArr;

  const selectedCountryIso = countryIso?.toLowerCase();
  const isGlobal = countryIso === GLOBAL_COUNTRY_ISO;
  const sortBy = RECOMMENDED_SORT_ITEM.id;
  const isSortByRecommended = sortBy === RECOMMENDED_SORT_ITEM.id;

  const variableCountryCode = isGlobal ? "" : selectedCountryIso;
  const variables: any = {
    ...(viewType === CUSTOM && identifier && { menuIdentifier: identifier }),
    storeId: storeParentId,
    designedStoreIds: [storeId],
    countryCode: variableCountryCode,
    globalZone: isGlobal,
    ...(!identifier && { taxonIds }),
    withSwag,
    budget: 0,
    page: 1,
    perPage: productCount,
    sortBy,
    skipWarehouse: !syncedToEs && !!storeCloneId,
    storeUpdatedAt: storeCacheKey,
    ...commonESVariables,
    ...getLockerShopESQueryVars({
      isLockerShop: isLegacyLockerShop,
    }),
    ...pricelessVariables,
    skipAggregations,
  };

  if (viewType === DEFAULT && (!isLockerShop || isSortByRecommended)) {
    variables.menuId = +menu.id;
    variables.menuItemId = +selectedCategory?.id;
  }

  const { allMenuTaxonIds, anyMenuWithSwag } = getAllMenuTaxons(menu?.allChildren);

  const isAllTab = categorySlug === ALL_PRODUCTS;
  if (viewType === DEFAULT && isAllTab) {
    variables.taxonIds = allMenuTaxonIds;
    variables.withSwag = anyMenuWithSwag;
  }

  if (isLockerShop) {
    variables.optimizedSearch = true;
    variables.withSwag = true;
  }

  Object.assign(variables, getFilterQueryVariablesFromFilterOptions(filterOptions));

  return variables;
};

export const getProductListingCarouselData = async ({
  dynamicSlicesData,
  categorySlug,
  productCount = DEFAULT_PRODUCT_COUNT,
  identifier,
  viewType = DEFAULT,
  filterOptions,
  country,
}: {
  dynamicSlicesData: any;
  categorySlug?: string;
  productCount?: number;
  identifier?: string;
  viewType?: string;
  filterOptions?: string;
  country?: { iso: string };
}) => {
  const { store, menu, menuMapping, countryIso } = dynamicSlicesData;

  const variables = queryVariables({
    store,
    menu,
    countryIso,
    menuMapping,
    categorySlug,
    productCount,
    identifier,
    viewType,
    filterOptions,
  });

  const selectedCategory = categorySlug ? menuMapping?.[categorySlug] : undefined;

  const menuList = menu?.children || [];
  const onlyHasAllProductsMenu = menuList.length === 1 && menuList[0].code === ALL_PRODUCTS;
  const shouldFetchProducts =
    identifier || (!onlyHasAllProductsMenu && menu && variables.taxonIds?.length);

  if (!shouldFetchProducts) {
    return { products: undefined, selectedCategory };
  }

  const productListData = await getProductListingData({
    country: country ?? { iso: countryIso },
    variables,
    store,
  });

  const products = productListData?.products;

  return { products, selectedCategory };
};
